// Listing modes for Operations Reports — Dashboard / Aircraft Hours / Pilot Hours.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`. Mirrored
// from src/aircraft_roster/modes.ts, itself mirrored from src/blank/BlankLayout.tsx
// (Rule 0 — the canonical reference).
//
//   <base>/dashboard       → OperationsReportsDashboard (the live bookings +
//                            flight-activity board this module started as)
//   <base>/aircraft-hours  → AircraftHoursFlown (legacy mode=aircraft_hours_flown_report)
//   <base>/pilot-hours     → PilotHoursFlown    (legacy mode=pilot_hours_flown_report)
//
// A hyphen in a segment is established here — src/action_register/modes.ts
// ships "kanban-status" / "kanban-assignee".

import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

/**
 * Mode segment -> breadcrumb / dropdown label. Keys ARE the child route paths
 * in index.tsx; adding one here without a matching <Route> gives a dropdown
 * entry that 404s against the module's own route table.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  dashboard: "Dashboard",
  "aircraft-hours": "Aircraft Hours Flown",
  "pilot-hours": "Pilot Hours Flown",
  flights: "Flight All Fields",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  dashboard: "Bookings and flight activity",
  "aircraft-hours": "Per aircraft, by period",
  "pilot-hours": "Per pilot, by period",
  flights: "Every flight, every column",
};

/**
 * Dashboard, because that is the surface this module already WAS before the
 * reports landed: `ops`'s bare `/app/` redirects here (OPS_LANDING_PRODUCTS in
 * src/app/App.tsx), so anyone arriving without an explicit mode must keep
 * landing on the board they had. The reports are additions to it, not a
 * replacement for it.
 */
export const DEFAULT_MODE_SEGMENT = "dashboard";

/**
 * `<module>.viewMode` — the shape every converted module uses. Underscored
 * even though the directory is hyphenated, matching `tech_log.viewMode` in
 * src/__tests__/routeModeSegments.test.tsx.
 *
 * NO legacy-value migration: this module never had a chip/menu switcher, so
 * nothing was ever stored under this key before now. Every read is either
 * absent (→ default) or a segment this file wrote.
 */
export const MODE_STORAGE_KEY = "operations_reports.viewMode";

/**
 * Is this a real mode segment?
 *
 * hasOwnProperty, NOT `in` — `in` walks the prototype chain, so "constructor",
 * "toString", "valueOf", "__proto__" and "hasOwnProperty" all test true against
 * an object literal. A layout validating with `in` would accept one as a mode
 * and redirect to it, 404ing the module against its own routes PERMANENTLY,
 * since the value is persisted. Regressions f74a205b / 2deb1dee; the prototype
 * chain values are enumerated in src/__tests__/routeModeSegments.test.tsx,
 * where this module is a registered target (added with these modes — the
 * comment is not a forward promise).
 */
export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

/** Redirect target for a bare visit to the module root. */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

/**
 * Split a live pathname into the module base and the mode segment. The base
 * is derived from the LIVE pathname rather than hardcoded, matching every
 * other converted module — this one is mounted from the tenant's own nav row
 * (`modules.mod_function`, live value `operations_reports`), so a hardcoded
 * base would break the moment a tenant mounts it under a different slug.
 */
export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  // Empty-check before prefixing the slash: a bare "/" + "" would make the
  // mode links "//dashboard", which a browser resolves as a PROTOCOL-RELATIVE
  // URL (host "dashboard") and navigates off-site.
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
